import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';
import { pwaInstallDismissedAtom } from '../stores/pwa.store';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandaloneMode(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) {
    return true;
  }
  return false;
}

export function usePwaInstallPrompt(): {
  isInstallable: boolean;
  promptInstall: () => Promise<void>;
  isDismissed: boolean;
} {
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const isDismissed = useAtomValue(pwaInstallDismissedAtom);

  useEffect(() => {
    if (isStandaloneMode()) {
      return;
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredPrompt.current = event as BeforeInstallPromptEvent;
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      deferredPrompt.current = null;
      setIsInstallable(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<void> => {
    if (!deferredPrompt.current) {
      return;
    }

    await deferredPrompt.current.prompt();
    await deferredPrompt.current.userChoice;

    deferredPrompt.current = null;
    setIsInstallable(false);
  };

  return { isInstallable, promptInstall, isDismissed };
}
